<template>
  <div class="event-sidebar">
    <component
      :is="isTabletDesktop ? 'div' : 'button'"
      :component="isTabletDesktop ? 'div' : 'button'"
      class="title-container"
      @click="!isTabletDesktop && handleMobileInfoToggle()"
    >
      <h1 class="event-title">
        {{ event.title }}
      </h1>

      <v-icon
        v-if="!isTabletDesktop"
        color="var(--color-white)"
        size="36"
      >
        {{ !isCollapsed ? mdiChevronUp : mdiChevronDown }}
      </v-icon>
    </component>

    <v-expand-transition>
      <div
        v-if="!isCollapsed || isTabletDesktop"
        class="collapsible-content"
      >
        <p class="paragraph">
          <v-icon
            class="icon"
            color="var(--color-white)"
          >
            {{ mdiCalendarMonth }}
          </v-icon>
          {{ event.dateFlat || date }}
        </p>
        <p class="paragraph">
          <v-icon
            class="icon"
            color="var(--color-white)"
          >
            {{ mdiClockOutline }}
          </v-icon>
          {{ event.timeFlat || time }}
        </p>
        <p
          v-if="event.locationAddress"
          class="paragraph"
        >
          <v-icon
            class="icon"
            color="var(--color-white)"
          >
            {{ mdiMapMarkerRadius }}
          </v-icon>
          <a
            :href="`https://www.google.com/maps/search/${event.locationAddress}`"
            class="link"
            target="_blank"
          >
            {{ event.locationAddress }}
          </a>
        </p>
        <p
          v-if="event.website"
          class="paragraph"
        >
          <v-icon
            class="icon"
            color="var(--color-white)"
          >
            {{ mdiLink }}
          </v-icon>
          <a
            :href="event.website"
            class="link"
            target="_blank"
          >
            Visit website to learn more
          </a>
        </p>
        <p
          v-if="event.paymentSystem?.priceRange"
          class="paragraph"
        >
          <v-icon
            class="icon"
            color="var(--color-white)"
          >
            {{ mdiCurrencyUsd }}
          </v-icon>
          {{ event.paymentSystem?.priceRange }}<br />
        </p>
        <div class="feedback-block">
          <EventFeedbackBlock
            v-if="!event.permissions.canEdit"
            :event="event"
          />
          <EventFeedbackBlockAdmin
            v-if="event.permissions.canEdit && event.isFinished"
            :event="event"
          />
        </div>
      </div>
    </v-expand-transition>

    <div class="event-sidebar-actions">
      <PurchaseTicketsButton
        v-if="!event.permissions.canEdit && !event.isFinished"
        class="btn-purchase-tickets"
        :event="event"
      />

      <BKCheckinButton
        v-if="!event.paymentSystem?.isEnabled && !event.permissions.canEdit && event.permissions.showCheckin"
        class="btn-front-desk"
        :event="event"
        @purchase="emit('reload:event')"
        @update:event="emit('update:event', $event)"
      />

      <BKButton
        v-if="showFrontdesk"
        class="btn-front-desk"
        color="var(--color-green)"
        white-text
        :to="
          event.paymentSystem?.isEnabled
            ? { name: 'EventFrontDesk', params: { eventId: String(event.id) } }
            : { name: 'Checkin', params: { id: String(event.id) } }
        "
      >
        Front Desk
      </BKButton>

      <BKButton
        v-if="event.permissions.canEdit"
        class="btn-edit-event"
        color="var(--color-white)"
        :to="{ name: 'EventEdit', params: { id: String(event.id) } }"
      >
        Edit Event
      </BKButton>

      <DisplayModeModal
        v-if="event.permissions.canEdit"
        class="btn-display-mode"
      />

      <EventPayoutTrigger
        v-if="event.permissions.canEdit && event.paymentSystem?.isEnabled"
        :event-id="event.id"
        class="payout"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BKCheckinButton } from '@/features/checkin'
import { getDateRangeFormatted, getTimeRangeFormatted } from '@/services/date'
import {
  mdiCalendarMonth,
  mdiChevronDown,
  mdiChevronUp,
  mdiClockOutline,
  mdiCurrencyUsd,
  mdiLink,
  mdiMapMarkerRadius,
} from '@mdi/js'
import { IEvent } from '@/enitites/event'
import { computed, ref } from 'vue'
import { useVuetify } from '@/services/useVuetify'
// @ts-expect-error Not a ts file
import store from '@/store'
import PurchaseTicketsButton from '@/features/tickets/ui/PurchaseTicketsButton.vue'
import EventPayoutTrigger from '@/pages/EventPage/ui/EventPayoutTrigger.vue'
import EventFeedbackBlock from './EventFeedbackBlock.vue'
import EventFeedbackBlockAdmin from './EventFeedbackBlockAdmin.vue'

const DisplayModeModal = () => import('@/enitites/display-mode/components/DisplayModeModal.vue')

const vuetify = useVuetify()
const isTabletDesktop = computed(() => vuetify.breakpoint.mdAndUp)
const props = defineProps<{
  event: IEvent
}>()

const emit = defineEmits<{
  (e: 'update:event', updatedEvent: IEvent): void
  (e: 'reload:event'): void
}>()

const getUserDB = computed(() => store.getters.getUserDB)

const isCollapsed = ref(true)
const date = computed(() => getDateRangeFormatted(props.event.dateStart, props.event.dateEnd, false))
const time = computed(() => getTimeRangeFormatted(props.event.dateStart, props.event.dateEnd))
const showFrontdesk = computed(
  () => props.event.permissions.canEdit || props.event.staff.hosts?.some(({ id }) => id === getUserDB.value?.id)
)

function handleMobileInfoToggle() {
  isCollapsed.value = !isCollapsed.value
}
</script>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.event-sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.title-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.event-title {
  @include font-h2;

  margin: 0;
  color: var(--color-white);
  font-weight: var(--font-weight-bold);

  @media (max-width: 600px) {
    @include font-h4;
  }
}

.collapsible-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.paragraph {
  display: flex;
  gap: 10px;
  color: var(--color-white);
  margin: 0;

  a {
    color: inherit;
  }
}

.feedback-block {
  max-width: 305px;
}

.event-sidebar-actions {
  display: grid;
  grid-template:
    'purchase-tickets purchase-tickets'
    'front-desk       front-desk'
    'edit-event       display-mode'
    'payout           payout'/ 1fr 1fr;
  gap: 10px;
  margin-top: auto;
  max-width: 305px;

  @media (max-width: 920px) {
    max-width: 100%;
  }

  .btn-purchase-tickets {
    grid-area: purchase-tickets;
  }

  .btn-front-desk {
    grid-area: front-desk;
  }

  .btn-edit-event {
    grid-area: edit-event;
  }

  .btn-display-mode {
    grid-area: display-mode;
  }
}

.payout {
  grid-area: payout;
}
</style>
